/* eslint-disable no-lone-blocks */
import classnames from "classnames";
import FeatherIcon from "feather-icons-react";
import PropTypes from "prop-types";
import { useCallback, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { Link, withRouter } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DeleteModal from "../../../Components/Common/DeleteModal";
import TableContainer from "../../../Components/Common/TableContainer";
import UserProfileAvatarWithFallback from "../../../Components/Common/UserProfileAvatarWithFallback";
import CertificationBadge from "../../../Components/Common/CertificationBadge";
import {
  CERTIFICATION_LEVEL_OPTIONS,
  getCertificationMeta,
} from "../../../constants/certification";
import { UserTypeMap } from "../../../Components/constants/all";
import { useDashboard } from "../../../hooks/useDashboard";
import {
  useBlockUser,
  useEnterprisesUsers,
  useOrganismesUsers,
  useParticularUsers,
  useUnblockUser,
  useUsersDirectory,
} from "../../../hooks/useUsersList";
import BlockUserModal from "../../../Components/Common/BlockUserModal";
import {
  useCertifyProfessional,
  useProfessionals,
} from "../../../hooks/useProfessionals";

//redux

/**
 * Stat cards of the users page, in display order.
 * Keys match the values of the backend `UserType` enum.
 */
const USER_TYPE_STAT_CARDS = [
  { key: "particular", label: "Particuliers", icon: "user" },
  { key: "professional", label: "Professionnels", icon: "tool" },
  { key: "enterprise", label: "Entreprises", icon: "briefcase" },
  { key: "bank", label: "Banques", icon: "credit-card" },
  { key: "organism", label: "Organismes", icon: "home" },
];

const Team = (props) => {
  document.title = props.t("Utilisateurs | Le Conseiller du BTP");

  const DEFAULT_USER_AVATAR =
    "https://prod.leconseillerdubtp.com/medias/default/user.png";
  const TEXT_FALLBACK = props.t("(Non Renseigné)");

  const dashboardQ = useDashboard();
  // The directory includes blocked accounts, unlike `/users/list/`: a blocked
  // user must stay visible here, otherwise nobody could ever unblock them.
  const usersQ = useUsersDirectory();
  const organismesQ = useOrganismesUsers();
  const enterprisesQ = useEnterprisesUsers();
  const professionalsQ = useProfessionals();
  const particularsQ = useParticularUsers();

  const certifyProfessional = useCertifyProfessional({ method: "patch" });
  const blockUserMutation = useBlockUser();
  const unblockUserMutation = useUnblockUser();

  const [blockModal, setBlockModal] = useState({
    open: false,
    mode: "block",
    user: null,
  });

  const openBlockModal = useCallback((user, mode) => {
    setBlockModal({ open: true, mode, user });
  }, []);

  const closeBlockModal = useCallback(() => {
    setBlockModal((prev) => ({ ...prev, open: false }));
  }, []);

  const confirmBlockAction = useCallback(
    (reason) => {
      const target = blockModal.user;
      if (!target) return;

      const onSuccess = () => closeBlockModal();
      if (blockModal.mode === "block") {
        blockUserMutation.mutate({ id: target.id, reason }, { onSuccess });
      } else {
        unblockUserMutation.mutate(target.id, { onSuccess });
      }
    },
    [blockModal, blockUserMutation, unblockUserMutation, closeBlockModal]
  );

  const asArray = (data) =>
    Array.isArray(data) ? data : data?.results ? data.results : [];

  const dashboard = dashboardQ.data || {};

  // Users page statistics: user TYPES only. Role-based counters (Admins,
  // Committee) belong to the dashboard, not here. The list is built from the
  // `users_by_type` payload returned by the API, so every type present in the
  // database shows up — including enterprises, banks and organisms, which had
  // no card before.
  const userTypeStats = useMemo(() => {
    const counts = new Map(
      (Array.isArray(dashboard.users_by_type) ? dashboard.users_by_type : []).map(
        (entry) => [entry.userType, entry.count]
      )
    );

    return USER_TYPE_STAT_CARDS.map((card) => ({
      ...card,
      value: counts.get(card.key) || 0,
    }));
  }, [dashboard.users_by_type]);

  const usersList = asArray(usersQ.data);
  const organismesList = asArray(organismesQ.data);
  const enterprisesList = asArray(enterprisesQ.data);
  const professionalsList = asArray(professionalsQ.data);
  const particularsList = asArray(particularsQ.data);

  const safeText = (value, fallback = TEXT_FALLBACK) => {
    if (value === null || value === undefined) return fallback;
    const s = String(value).trim();
    return s.length ? s : fallback;
  };

  const [customActiveTab, setcustomActiveTab] = useState("1");
  const toggleCustom = (tab) => {
    if (customActiveTab !== tab) {
      setcustomActiveTab(tab);
    }
  };

  const [certifyModalOpen, setCertifyModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [certificateValue, setCertificateValue] = useState(1);

  const openCertifyModal = useCallback((user) => {
    setSelectedUser(user);
    const existing = user?.professional?.banca_certificate;
    const initialValue =
      typeof existing === "number" && Number.isFinite(existing) ? existing : 1;
    setCertificateValue(initialValue);
    setCertifyModalOpen(true);
  }, []);

  const submitCertification = useCallback(async () => {
    const professionalId = selectedUser?.professional?.id;
    if (!professionalId) return;

    try {
      await certifyProfessional.mutateAsync({
        id: professionalId,
        payload: { banca_certificate: Number(certificateValue) },
      });
      setCertifyModalOpen(false);
      setSelectedUser(null);
    } catch (_err) {
      // handled by hook toast
    }
  }, [certificateValue, certifyProfessional, selectedUser]);

  //delete order
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteModalMulti, setDeleteModalMulti] = useState(false);

  const deleteMultiple = () => {
    const ele = document.querySelectorAll(".productCheckBox:checked");
    const del = document.getElementById("selection-element");
    ele.forEach((element) => {
      del.style.display = "none";
    });
  };

  const formatUserData = {
    getUserType: (type) => {
      return type && UserTypeMap[type] ? (
        <h5 className={`badge ${UserTypeMap[type].class}`}>
          {UserTypeMap[type].label}
        </h5>
      ) : null;
    },
  };

  const createColumns = (props, { handleContactClick = null } = {}) =>
    [
      {
        Header: props.t("IMAGE"),
        accessor: "profile_pic",
        Cell: (recruitments) => (
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              <UserProfileAvatarWithFallback
                picture={recruitments.row.original?.profile?.profile_pic}
                name={
                  recruitments.row.original?.profile?.last_name ||
                  recruitments.row.original?.username ||
                  recruitments.row.original?.email
                }
                defaultUrl={DEFAULT_USER_AVATAR}
                t={props.t}
              />
            </div>
          </div>
        ),
      },
      {
        Header: props.t("NOM"),
        accessor: "last_name",
        Cell: (recruitments) => (
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              <h5
                className="fs-14 mb-1 italic"
                style={{ textTransform: "uppercase", fontWeight: "bold" }}
              >
                <Link className="text-dark">
                  {safeText(
                    recruitments.row.original?.profile?.last_name,
                    safeText(recruitments.row.original?.username, TEXT_FALLBACK)
                  )}
                </Link>
              </h5>
            </div>
          </div>
        ),
      },
      {
        Header: props.t("PRENOM(S)"),
        accessor: "first_name",
        Cell: (recruitments) => (
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              <h5 className="fs-14 mb-1 italic" style={{ fontWeight: "bold" }}>
                <Link className="text-dark">
                  {safeText(
                    recruitments.row.original?.profile?.first_name,
                    TEXT_FALLBACK
                  )}
                </Link>
              </h5>
            </div>
          </div>
        ),
      },
      {
        Header: props.t("EMAIL"),
        accessor: "email",
        Cell: (recruitments) => (
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              <h5 className="fs-14 mb-1 uppercase">
                {safeText(recruitments.row.original.email, TEXT_FALLBACK)}
              </h5>
            </div>
          </div>
        ),
      },
      {
        Header: props.t("TELEPHONE"),
        accessor: "phone_number",
        Cell: (recruitments) => (
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              <h5 className="fs-14 mb-1 uppercase">
                {safeText(
                  recruitments.row.original.phone_number,
                  TEXT_FALLBACK
                )}
              </h5>
            </div>
          </div>
        ),
      },
      {
        Header: props.t("TYPE"),
        accessor: "user_type",
        Cell: (recruitments) => (
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              {formatUserData.getUserType(
                safeText(
                  recruitments.row.original?.profile?.user_type,
                  recruitments.row.original?.user_type
                )
              )}
            </div>
          </div>
        ),
      },
      {
        Header: props.t("PAYS"),
        accessor: "country",
        Cell: (recruitments) => (
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              <h5 className="fs-11 uppercase badge badge-soft-warning">
                {safeText(
                  recruitments.row.original?.profile?.country,
                  TEXT_FALLBACK
                )}
              </h5>
            </div>
          </div>
        ),
      },
      {
        // The backend now returns the resolved label (`category`) alongside
        // the id, so there is no client-side lookup any more — that lookup was
        // what always rendered "Non renseigné".
        Header: props.t("Catégorie d'institution"),
        accessor: "profile.category",
        Cell: (recruitments) => {
          const categoryName = safeText(
            recruitments.row.original.profile?.category,
            "Non renseigné"
          );
          const isEmpty = categoryName === "Non renseigné";

          return (
            <div className="d-flex align-items-center">
              <div className="flex-grow-1">
                <h5
                  className={`fs-11 uppercase badge badge-soft-${
                    isEmpty ? "warning" : "primary"
                  }`}
                >
                  {categoryName}
                </h5>
              </div>
            </div>
          );
        },
      },
      {
        Header: props.t("Institution"),
        accessor: "profile.subcategory",
        Cell: (recruitments) => {
          const subcategoryName = safeText(
            recruitments.row.original.profile?.subcategory,
            "Non renseigné"
          );
          const isEmpty = subcategoryName === "Non renseigné";

          return (
            <div className="d-flex align-items-center">
              <div className="flex-grow-1">
                <h5
                  className={`fs-11 uppercase badge badge-soft-${
                    isEmpty ? "info" : "success"
                  }`}
                >
                  {subcategoryName}
                </h5>
              </div>
            </div>
          );
        },
      },
      {
        Header: props.t("STATUT"),
        accessor: "is_active",
        Cell: (recruitments) => {
          const user = recruitments.row.original;
          const isBlocked = user.is_active === false;

          return (
            <div className="d-flex align-items-center">
              <div className="flex-grow-1">
                {isBlocked ? (
                  <span
                    className="badge badge-soft-danger fs-11"
                    title={user.blocked_reason || undefined}
                  >
                    <i className="ri-lock-line align-bottom me-1"></i>
                    Bloqué
                  </span>
                ) : (
                  <span className="badge badge-soft-success fs-11">Actif</span>
                )}
              </div>
            </div>
          );
        },
      },
      {
        Header: props.t("ACTIONS"),
        Cell: (recruitments) => {
          const user = recruitments.row.original;
          const isBlocked = user.is_active === false;

          return (
            <div className="d-flex align-items-center gap-2">
              <Link
                to={`/profile-users/${user.id}/`}
                className="text-primary d-inline-block"
                title={props.t("Voir la fiche")}
              >
                <i className="ri-eye-fill align-bottom fs-16" />
              </Link>
              {isBlocked ? (
                <Link
                  to="#"
                  className="text-success d-inline-block"
                  title={props.t("Réactiver l'accès")}
                  onClick={(e) => {
                    e.preventDefault();
                    openBlockModal(user, "unblock");
                  }}
                >
                  <i className="ri-lock-unlock-line align-bottom fs-16" />
                </Link>
              ) : (
                <Link
                  to="#"
                  className="text-danger d-inline-block"
                  title={props.t("Bloquer l'accès")}
                  onClick={(e) => {
                    e.preventDefault();
                    openBlockModal(user, "block");
                  }}
                >
                  <i className="ri-lock-line align-bottom fs-16" />
                </Link>
              )}
            </div>
          );
        },
      },
      handleContactClick && {
        Header: props.t(" "),
        accessor: "certification",
        Cell: (recruitments) => (
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              {recruitments.row.original.professional?.is_certified ? (
                <CertificationBadge
                  professional={recruitments.row.original.professional}
                />
              ) : (
                <Button
                  className="btn btn-soft-warning"
                  style={{ fontSize: "11px" }}
                  onClick={() => handleContactClick(recruitments.row.original)}
                  disabled={certifyProfessional.isPending}
                >
                  Certifier
                </Button>
              )}
            </div>
          </div>
        ),
      },
    ].filter(Boolean);

  const columns = useMemo(
    () => createColumns(props),
    [props, certifyProfessional.isPending, openBlockModal]
  );

  const columns3 = useMemo(
    () =>
      createColumns(props, {
        handleContactClick: openCertifyModal,
      }),
    [props, openCertifyModal, certifyProfessional.isPending, openBlockModal]
  );

  const isUsersLoading = usersQ.isLoading;
  const isOrganismesLoading = organismesQ.isLoading;
  const isEnterprisesLoading = enterprisesQ.isLoading;
  const isProfessionalsLoading = professionalsQ.isLoading;
  const isParticularsLoading = particularsQ.isLoading;
  const dashboardErrorMessage = dashboardQ.error?.message || null;
  const usersErrorMessage = usersQ.error?.message || null;
  const organismesErrorMessage = organismesQ.error?.message || null;
  const enterprisesErrorMessage = enterprisesQ.error?.message || null;
  const professionalsErrorMessage = professionalsQ.error?.message || null;
  const particularsErrorMessage = particularsQ.error?.message || null;

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} />

      <BlockUserModal
        show={blockModal.open}
        mode={blockModal.mode}
        user={blockModal.user}
        onConfirm={confirmBlockAction}
        onCloseClick={closeBlockModal}
        isPending={
          blockUserMutation.isPending || unblockUserMutation.isPending
        }
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={() => setDeleteModal(false)}
        onCloseClick={() => setDeleteModal(false)}
      />
      <DeleteModal
        show={deleteModalMulti}
        onDeleteClick={() => {
          deleteMultiple();
          setDeleteModalMulti(false);
        }}
        onCloseClick={() => setDeleteModalMulti(false)}
      />
      <Container fluid>
        <BreadCrumb
          title={props.t("Liste des utilisateurs du BTP")}
          pageTitle={props.t("Listes des utilisateurs du BTP")}
        />
        {dashboardQ.isError ? (
          <div className="alert alert-danger" role="alert">
            {dashboardErrorMessage || props.t("Une erreur est survenue")}
          </div>
        ) : null}

        <Modal
          isOpen={certifyModalOpen}
          toggle={() => setCertifyModalOpen(false)}
        >
          <ModalHeader toggle={() => setCertifyModalOpen(false)}>
            {props.t("Certification")}
          </ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <label className="form-label" htmlFor="team-certification-level">
                {props.t("Niveau de certification")}
              </label>
              <Input
                id="team-certification-level"
                type="select"
                className="form-select"
                value={certificateValue}
                onChange={(e) => setCertificateValue(e.target.value)}
              >
                {CERTIFICATION_LEVEL_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Input>
              <small className="text-muted">
                {getCertificationMeta(certificateValue).description}
              </small>
              {Number(certificateValue) === 0 ? (
                <div className="alert alert-warning mt-2 mb-0 fs-12">
                  <i className="ri-alert-line align-bottom me-1"></i>
                  {props.t(
                    "Le niveau 0 retire la certification de ce professionnel."
                  )}
                </div>
              ) : null}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              type="button"
              color="secondary"
              onClick={() => setCertifyModalOpen(false)}
            >
              {props.t("Annuler")}
            </Button>
            <Button
              type="button"
              color="success"
              onClick={submitCertification}
              disabled={certifyProfessional.isPending}
            >
              {props.t("Valider")}
            </Button>
          </ModalFooter>
        </Modal>

        <Row className="text-center flex justify-items-center">
          {userTypeStats.map((stat) => (
            <Col md={4} key={stat.key}>
              <Card className="card-animate">
                <CardBody>
                  <div className="d-flex justify-content-between">
                    <div>
                      <p className="fw-medium text-muted mb-0">{stat.label}</p>
                      <h2 className="mt-4 ff-secondary fw-semibold">
                        <span className="counter-value">{stat.value}</span>
                      </h2>
                    </div>
                    <div>
                      <div className="avatar-sm flex-shrink-0">
                        <span className="avatar-title bg-soft-warning rounded-circle fs-2">
                          <FeatherIcon icon={stat.icon} className="text-warning" />
                        </span>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>

        <br></br>

        <Nav tabs className="nav-tabs-custom nav-warning">
          <NavItem>
            <NavLink
              style={{ cursor: "pointer", fontWeight: "bold" }}
              className={classnames({
                active: customActiveTab === "1",
              })}
              onClick={() => {
                toggleCustom("1");
              }}
            >
              {props.t("TOUS LES UTILISATEURS")}
            </NavLink>
          </NavItem>

          {/* organismes */}

          <NavItem>
            <NavLink
              style={{ cursor: "pointer", fontWeight: "bold" }}
              className={classnames({
                active: customActiveTab === "2",
              })}
              onClick={() => {
                toggleCustom("2");
              }}
            >
              {props.t("ORGANISMES")}
            </NavLink>
          </NavItem>

          {/* Entreprises */}

          <NavItem>
            <NavLink
              style={{ cursor: "pointer", fontWeight: "bold" }}
              className={classnames({
                active: customActiveTab === "3",
              })}
              onClick={() => {
                toggleCustom("3");
              }}
            >
              {props.t("ENTREPRISES")}
            </NavLink>
          </NavItem>

          {/* professionnels */}

          <NavItem>
            <NavLink
              style={{ cursor: "pointer", fontWeight: "bold" }}
              className={classnames({
                active: customActiveTab === "4",
              })}
              onClick={() => {
                toggleCustom("4");
              }}
            >
              {props.t("PROFESSIONNELS")}
            </NavLink>
          </NavItem>

          {/* particuliers */}
          <NavItem>
            <NavLink
              style={{ cursor: "pointer", fontWeight: "bold" }}
              className={classnames({
                active: customActiveTab === "5",
              })}
              onClick={() => {
                toggleCustom("5");
              }}
            >
              {props.t("PARTICULIERS")}
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent
          activeTab={customActiveTab}
          className=" border-top-0 "
          id="nav-tabContent"
        >
          <TabPane id="nav-speci" tabId="1">
            <TabContentSection
              isLoading={isUsersLoading}
              isError={usersQ.isError}
              errorMessage={usersErrorMessage}
              data={usersList}
              columns={columns}
              props={props}
              onDeleteMulti={() => setDeleteModalMulti(true)}
            />
          </TabPane>
        </TabContent>

        <TabContent
          activeTab={customActiveTab}
          className=" border-top-0 "
          id="nav-tabContent"
        >
          <TabPane id="nav-speci" tabId="2">
            <TabContentSection
              isLoading={isOrganismesLoading}
              isError={organismesQ.isError}
              errorMessage={organismesErrorMessage}
              data={organismesList}
              columns={columns}
              props={props}
              onDeleteMulti={() => setDeleteModalMulti(true)}
            />
          </TabPane>
        </TabContent>

        <TabContent
          activeTab={customActiveTab}
          className=" border-top-0 "
          id="nav-tabContent"
        >
          <TabPane id="nav-speci" tabId="3">
            <TabContentSection
              isLoading={isEnterprisesLoading}
              isError={enterprisesQ.isError}
              errorMessage={enterprisesErrorMessage}
              data={enterprisesList}
              columns={columns}
              props={props}
              onDeleteMulti={() => setDeleteModalMulti(true)}
            />
          </TabPane>
        </TabContent>

        <TabContent
          activeTab={customActiveTab}
          className=" border-top-0 "
          id="nav-tabContent"
        >
          <TabPane id="nav-speci" tabId="4">
            <TabContentSection
              isLoading={isProfessionalsLoading}
              isError={professionalsQ.isError}
              errorMessage={professionalsErrorMessage}
              data={professionalsList}
              columns={columns3}
              props={props}
              onDeleteMulti={() => setDeleteModalMulti(true)}
            />
          </TabPane>
        </TabContent>

        <TabContent
          activeTab={customActiveTab}
          className=" border-top-0 "
          id="nav-tabContent"
        >
          <TabPane id="nav-speci" tabId="5">
            <TabContentSection
              isLoading={isParticularsLoading}
              isError={particularsQ.isError}
              errorMessage={particularsErrorMessage}
              data={particularsList}
              columns={columns}
              props={props}
              onDeleteMulti={() => setDeleteModalMulti(true)}
            />
          </TabPane>
        </TabContent>
      </Container>
    </div>
  );
};

const TabContentSection = ({
  isLoading,
  isError,
  errorMessage,
  data,
  columns,
  props,
  onDeleteMulti,
}) => (
  <div>
    <Row>
      <div className="col-xl-12 col-lg-12">
        <div>
          <div className="card">
            <div className="card-header border-0">
              <div className="row align-items-center">
                <div className="col-auto">
                  <div id="selection-element">
                    <div className="my-n1 d-flex align-items-center text-muted">
                      Select{" "}
                      <div
                        id="select-content"
                        className="text-body fw-semibold px-1"
                      >
                        {}
                      </div>{" "}
                      Result{" "}
                      <button
                        type="button"
                        className="btn btn-link link-danger p-0 ms-3"
                        onClick={onDeleteMulti}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body pt-0">
              {isLoading ? (
                <div className="py-4 text-center">
                  <div>
                    <lord-icon
                      src="https://cdn.lordicon.com/xjovhxra.json"
                      trigger="loop"
                      colors="primary:#b38a12,secondary:#02376b"
                      style={{ width: "72px", height: "72px" }}
                    ></lord-icon>
                  </div>
                  <div className="mt-4">
                    <h5>{props.t("Chargement")}...</h5>
                  </div>
                </div>
              ) : isError ? (
                <div className="py-4 text-center">
                  <div className="mt-4">
                    <h5>{errorMessage || props.t("Une erreur est survenue")}</h5>
                  </div>
                </div>
              ) : data && data.length >= 0 ? (
                <TableContainer
                  columns={columns}
                  data={data || []}
                  isAddUserList={false}
                  customPageSize={10}
                  divClass="table-responsive mb-1"
                  tableClass="mb-0 align-middle"
                  theadClass="table-light text-muted"
                  isMetierFilter={false}
                  isGlobalFilter={true}
                />
              ) : (
                <div className="py-4 text-center">
                  <div className="mt-4">
                    <h5>{props.t("Désolé, pas de resultats trouvés")}</h5>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Row>
  </div>
);

Team.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};
export default withRouter(withTranslation()(Team));
